// library imports
import { useState, useEffect, useContext } from "react";
import {
  Row,
  Col,
  DatePicker,
  Collapse,
  Grid,
  Form,
  Upload,
  Button,
  Image,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

// hook imports
import useFetch from "../../hooks/useFetch";

// context imports
import { IndexContext } from "../../context/IndexContext";

// component imports
import CustomEditTable from "../../components/common/CustomEditTable";

const { useBreakpoint } = Grid;

export default function AbatementDataEntry() {
  const screens = useBreakpoint();

  const { siteSelected, notificationApi, messageApi } =
    useContext(IndexContext);
  const [fetchWrapper] = useFetch();

  // selected month year
  const [selectedMonthYear, setSelectedMonthYear] = useState(
    dayjs().endOf("M")
  );

  // custom edit table related props
  const [form] = Form.useForm(); // form instance
  const [data, setData] = useState([]); // data for the abatement table

  // collapse items related props
  const [parentActiveKey, setParentActiveKey] = useState([]); // this key is used to select correct file for abatement source and dimension selection

  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [fileList, setFileList] = useState([]);

  // abatement table columns
  const columns = [
    {
      title: "Activity Name",
      dataIndex: "ActivityName",
      inputType: "input",
      width: "240px",
      editable: true,
      required: true,
    },
    {
      title: "Quantity",
      dataIndex: "Quantity",
      inputType: "number",
      width: "240px",
      editable: true,
      required: true,
    },
    {
      title: "Unit",
      dataIndex: "Unit",
      inputType: "input",
      editable: true,
      width: "240px",
      required: true,
    },
    {
      title: "Factor Value (tCO₂e/Unit)",
      dataIndex: "EmissionFactor",
      inputType: "number",
      editable: true,
      width: "240px",
      required: true,
    },
    {
      title: "Total Abatement",
      dataIndex: "TotalAbatement",
      inputType: "input",
      width: "240px",
    },
    {
      title: "Note",
      dataIndex: "Note",
      inputType: "input",
      width: "240px",
      editable: true,
    },
  ];

  // parent scope items
  const scopeItems = [
    {
      key: "scope_1",
      label: (
        <Row>
          <Col xs={24} sm={24} md={16}>
            {screens.md ? "Scope 1: Direct Carbon Abatement" : "Scope 1"}
          </Col>
        </Row>
      ),
      children: (
        <CustomEditTable
          name={"Abatement"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          saveData={saveAbatementData}
          updateData={updateAbatementData}
          deleteData={deleteAbatementData}
          addButtonText="Add Abatement"
          loading={loading}
        />
      ),
    },
    {
      key: "scope_2",
      label: (
        <Row>
          <Col xs={24} sm={24} md={16}>
            {screens.md ? "Scope 2: Electricity Carbon Abatement" : "Scope 2"}
          </Col>
        </Row>
      ),
      children: (
        <CustomEditTable
          name={"Abatement"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          saveData={saveAbatementData}
          updateData={updateAbatementData}
          deleteData={deleteAbatementData}
          addButtonText="Add Abatement"
          loading={loading}
        />
      ),
    },
    {
      key: "scope_3",
      label: (
        <Row>
          <Col xs={24} sm={24} md={16}>
            {screens.md
              ? "Scope 3: Other Indirect Carbon Abatement"
              : "Scope 3"}
          </Col>
        </Row>
      ),
      children: (
        <CustomEditTable
          name={"Abatement"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          saveData={saveAbatementData}
          updateData={updateAbatementData}
          deleteData={deleteAbatementData}
          addButtonText="Add Abatement"
          loading={loading}
        />
      ),
    },
  ];

  /** API to get all abatement data */
  function getAllAbatementData() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "abatement_data_entry/getAllAbatementData",
      method: "GET",
      params: {
        month_year: selectedMonthYear.endOf("M").format("YYYY-MM-DD"),
        site_ID: siteSelected,
        parent_active_key: parentActiveKey[0],
      },
      set_content_type_header: false,
      onSuccess: (response) => {
        setData(response.data);
        setLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setLoading(false);
      },
    });
  }

  /** API to save data to backend */
  function saveAbatementData(newData) {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "abatement_data_entry/saveAbatementData",
      method: "POST",
      body: {
        data: newData,
        site_ID: siteSelected,
        month_year: selectedMonthYear.endOf("M").format("YYYY-MM-DD"),
        parent_active_key: parentActiveKey[0],
      },
      onSuccess: (response) => {
        notificationApi.success({
          message: response.message,
        });
        getAllAbatementData();
        setLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setLoading(false);
      },
    });
  }

  /** API to save edited abatement data */
  function updateAbatementData(abatement_ID_to_be_edited, editedData) {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "abatement_data_entry/updateAbatementData",
      method: "POST",
      body: {
        data: editedData,
        abatement_ID: abatement_ID_to_be_edited,
      },
      onSuccess: (response) => {
        notificationApi.success({
          message: response.message,
        });
        getAllAbatementData();
        setLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setLoading(false);
      },
    });
  }

  /** API to delete abatement data */
  function deleteAbatementData(abatement_ID_to_be_deleted) {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "abatement_data_entry/deleteAbatementData",
      method: "POST",
      body: {
        abatement_ID: abatement_ID_to_be_deleted,
      },
      onSuccess: (response) => {
        notificationApi.success({
          message: response.message,
        });
        getAllAbatementData();
        setLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setLoading(false);
      },
    });
  }

  //handle upload
  function handleUpload(upload_detail) {
    // indicate uploading is performing
    setUploadLoading(true);

    const formData = new FormData();
    formData.append("uploaded-files", upload_detail.file);
    formData.append("site_id", siteSelected);
    formData.append("date", selectedMonthYear.endOf("M").format("YYYY-MM-DD"));

    fetchWrapper({
      endpoint_url: "abatement_data_entry/uploadFile",
      set_content_type_header: false,
      body: formData,
      onSuccess: (data) => {
        if ("error" in data) {
          upload_detail.onError("File Processing Failed");
          messageApi.open({
            type: "error",
            content: data.error,
          });
        } else {
          //upload success
          upload_detail.onSuccess("File processed successfully!");
          messageApi.open({
            type: "success",
            content: data.msg,
          });
          getBackendFileList();
        }
        setUploadLoading(false);
      },
      onFetchError: (error) => {
        upload_detail.onError(`Error: ${error}`);
        setUploadLoading(false);
      },
    });
  }

  function getBackendFileList() {
    fetchWrapper({
      endpoint_url: "abatement_data_entry/getBackendFileList",
      method: "POST",
      body: {
        site_ID: siteSelected,
        month_year: selectedMonthYear.endOf("M").format("YYYY-MM-DD"),
      },
      onSuccess: (response) => {
        setFileList(response.data);
        console.log(response.data);
      },
      onResponseError: (response) => {},
    });
  }

  function deleteFile(file) {
    // indicate deleting is performing
    setDeleteLoading(true);

    const formData = new FormData();
    formData.append("ID", file.uid);

    fetchWrapper({
      endpoint_url: "abatement_data_entry/deleteFile",
      set_content_type_header: false,
      body: formData,
      onSuccess: (data) => {
        if ("error" in data) {
          messageApi.open({
            type: "error",
            content: data.error,
          });
        } else {
          //delete success
          messageApi.open({
            type: "success",
            content: data.msg,
          });
          getBackendFileList();
        }
        setUploadLoading(false);
      },
      onFetchError: (error) => {
        setDeleteLoading(false);
      },
    });
  }

  useEffect(() => {
    if (siteSelected && parentActiveKey.length > 0) {
      getAllAbatementData();
    }
  }, [siteSelected, parentActiveKey, selectedMonthYear]);

  useEffect(() => {
    if (siteSelected && selectedMonthYear) {
      getBackendFileList();
    }
  }, [siteSelected, selectedMonthYear]);

  return (
    <>
      <Row className="pb-2">
        <Col
          md={12}
          lg={8}
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <div>Month:</div>
          <DatePicker
            picker="month"
            value={selectedMonthYear}
            style={{ width: 200, marginLeft: "8px" }}
            onChange={(value) => {
              if (value) {
                setSelectedMonthYear(value);
              }
            }}
          />
        </Col>
      </Row>
      <Collapse
        accordion
        items={scopeItems}
        onChange={(key) => {
          setParentActiveKey(key);
        }}
        destroyInactivePanel={true}
      />

      <div
        style={{
          padding: "16px 0px",
          fontSize: 16,
          fontWeight: "bold",
        }}
      >
        Supporting Documents
      </div>

      <Upload
        fileList={fileList}
        customRequest={handleUpload}
        onRemove={deleteFile}
        rootClassName="upload-button-max-width"
      >
        <Button
          type="primary"
          icon={<UploadOutlined />}
          loading={uploadLoading}
          style={{ width: "100%" }}
        >
          Click to Upload
        </Button>
      </Upload>
    </>
  );
}
